'use client';

import {useEffect, useRef} from 'react';
import {trackViewItem} from '@/components/cookie-consent/utils.tracking';

interface ProductViewTrackerProps {
    variantId: string;
    title: string;
    categoryName?: string;
    brandName?: string;
    price?: number;
}

/**
 * Client component that fires a GA4 view_item event when a product page is viewed.
 * Place this on product detail pages. Renders nothing visible.
 */
export function ProductViewTracker({variantId, title, categoryName, brandName, price}: ProductViewTrackerProps) {
    const tracked = useRef(false);

    useEffect(() => {
        if (tracked.current) return;

        // A.9 — deprecated / unavailable products pass price=0 because the
        // product JSON has no current offer. Sending view_item with value=0
        // skews GA4 averages (Ø price, eventValue). Skip the track call until
        // price is a real number > 0. The tracked-ref stays false, so the
        // effect re-runs when price arrives (SSR hydration / suspense boundary).
        if (!price || price <= 0) return;

        tracked.current = true;

        trackViewItem({
            item_id: variantId,
            item_name: title,
            item_category: categoryName,
            item_brand: brandName,
            price,
            currency: 'EUR',
        });
    }, [variantId, title, categoryName, brandName, price]);

    return null;
}
